import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService } from '../catalog/catalog.service';
import { CreateConsignmentContractPayload, ConsignmentStockRecord, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-consignment-contract-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, CustomerSelectorComponent, CurrencySelectorComponent, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.consignments.form.createTitle') : t('sales.consignments.form.editTitle')" [description]="t('sales.consignments.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card"><a class="back-link" [routerLink]="['/sales/consignment-contracts']">{{ t('catalog.action.backToList') }}</a></header>
        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.consignments.fields.contractNumber') }}</span><input pInputText [(ngModel)]="form.contractNumber" /></label>
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
            <label>
              <span>{{ t('sales.consignments.fields.offering') }}</span>
              <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.offeringId" [filter]="true" appendTo="body" />
            </label>
            <label><span>{{ t('sales.consignments.fields.quantity') }}</span><p-inputNumber [(ngModel)]="form.quantity" [min]="0" /></label>
            <label><span>{{ t('sales.consignments.fields.effectiveFrom') }}</span><ain-date-input [(ngModel)]="form.effectiveFromUtc" /></label>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </section>
        } @else {
          <div class="tabs">
            <button type="button" [class.active]="activeTab()==='stock'" (click)="activeTab.set('stock')">{{ t('sales.consignments.tabs.stock') }}</button>
            <button type="button" [class.active]="activeTab()==='settle'" (click)="activeTab.set('settle')">{{ t('sales.consignments.tabs.settle') }}</button>
          </div>
          @if (activeTab()==='stock') {
            <p-table [value]="stock()"><ng-template pTemplate="header"><tr><th>{{ t('sales.consignments.fields.offering') }}</th><th>{{ t('sales.consignments.fields.onHand') }}</th><th>{{ t('sales.consignments.fields.remaining') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ offeringLabel(row.offeringId) }}</td><td>{{ row.quantityOnHand }}</td><td>{{ row.remainingQuantity }}</td></tr></ng-template></p-table>
          }
          @if (activeTab()==='settle') {
            <section class="card grid">
              <label><span>{{ t('sales.consignments.fields.settleQuantity') }}</span><p-inputNumber [(ngModel)]="settleForm.quantity" [min]="0" /></label>
              <label><span>{{ t('sales.consignments.fields.settleAmount') }}</span><p-inputNumber [(ngModel)]="settleForm.amount" [min]="0" /></label>
              <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="settleForm.currencyCode" /></label>
              <button pButton type="button" [label]="t('sales.consignments.settle')" (click)="settle()"></button>
            </section>
          }
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px}.back-link{color:#1d4ed8;font-weight:600;text-decoration:none}.grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}label{display:grid;gap:6px}.tabs{display:flex;gap:8px;margin-bottom:10px}.tabs button{border:1px solid #cbd5e1;border-radius:999px;padding:6px 12px;cursor:pointer}.tabs button.active{background:#1d4ed8;color:#fff}.error{color:#b91c1c}`]
})
export class SalesConsignmentContractDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly catalog = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal<'stock' | 'settle'>('stock');
  readonly stock = signal<ConsignmentStockRecord[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  form = { contractNumber: '', customerAccountId: '' as string | null, offeringId: '', quantity: 1, effectiveFromUtc: null as string | null };
  settleForm = { quantity: 0, amount: 0, currencyCode: 'IRR' };
  ngOnInit(): void {
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows),
      error: () => this.offerings.set([])
    });
    const id = this.route.snapshot.paramMap.get('contractId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) { this.loading.set(false); return; }
    this.loadStock(id!);
  }
  offeringOptions(): { label: string; value: string }[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }
  offeringLabel(offeringId: string): string {
    const match = this.offerings().find(item => item.id === offeringId);
    return match ? `${match.code} - ${match.title}` : offeringId;
  }
  loadStock(id: string): void {
    this.api.listConsignmentStock(id).subscribe({ next: s => { this.stock.set(s); this.loading.set(false); } });
  }
  save(): void {
    if (!this.form.customerAccountId || !this.form.offeringId) return;
    const payload: CreateConsignmentContractPayload = {
      contractNumber: this.form.contractNumber,
      customerAccountId: this.form.customerAccountId,
      effectiveFromUtc: this.form.effectiveFromUtc ?? new Date().toISOString(),
      stockLines: [{ offeringId: this.form.offeringId, quantityOnHand: this.form.quantity }]
    };
    this.api.createConsignmentContract(payload).subscribe({
      next: c => void this.router.navigate(['/sales/consignment-contracts', c.id])
    });
  }
  settle(): void {
    const id = this.route.snapshot.paramMap.get('contractId')!;
    const line = this.stock()[0];
    if (!line) return;
    this.api.settleConsignment(id, {
      consignmentStockId: line.id,
      settledQuantity: this.settleForm.quantity,
      settledAmount: this.settleForm.amount,
      currencyCode: this.settleForm.currencyCode,
      idempotencyKey: `settle-${Date.now()}`
    }).subscribe({ next: () => this.loadStock(id) });
  }
  t(key: string): string { return this.localization.translate(key); }
}
